import { FC, useState } from 'react'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'
import { HeadSection } from 'src/components/elements'
import { Button } from 'src/components/ui'
import { CreateRequestModal } from 'src/components/elements/modals'
import PropertyList from '../property/PropertyList'
import RequestsList from '../requests/RequestsList'

type InboxView = 'outgoing' | 'incoming'

// Unified requests inbox (roadmap 41 D2/D3). One place on /cabinet with a
// Вихідні|Вхідні switch, replacing the two separate sections that each carried
// their own "Усі" tab. Вихідні = the user's own saved searches and their
// matches (+ create); Вхідні = seekers' requests matched to the user's
// listings. Only one view renders at a time, so there is a single "Усі".
const RequestsInbox: FC = () => {
  const { t } = useTranslation()
  const [view, setView] = useState<InboxView>('outgoing')
  const [openCreate, setOpenCreate] = useState(false)

  const views: { value: InboxView; label: string }[] = [
    { value: 'outgoing', label: t('requests_inbox.outgoing') },
    { value: 'incoming', label: t('requests_inbox.incoming') },
  ]

  return (
    <section className="flex flex-col gap-4">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <HeadSection title={t('requests_inbox.title')} />
        {view === 'outgoing' && (
          <Button size="sm" onClick={() => setOpenCreate(true)}>
            {t('requests_inbox.create')}
          </Button>
        )}
      </div>

      {/* one segmented switch instead of two pill buttons */}
      <div
        role="tablist"
        aria-label={t('requests_inbox.title')}
        className="flex gap-1 p-1 h-11 w-full md:w-auto md:self-start rounded-xl bg-track"
      >
        {views.map((option) => (
          <button
            key={option.value}
            id={`requests-inbox-tab-${option.value}`}
            type="button"
            role="tab"
            aria-selected={view === option.value}
            aria-controls="requests-inbox-panel"
            onClick={() => setView(option.value)}
            className={clsx(
              'flex-1 md:flex-none px-4 rounded-[0.5625rem] text-sm transition-colors duration-200',
              view === option.value
                ? 'bg-raised font-extrabold text-ink shadow-[0_1px_4px_rgba(33,36,48,0.12)]'
                : 'font-bold text-muted hover:text-ink',
            )}
          >
            {option.label}
          </button>
        ))}
      </div>

      <CreateRequestModal open={openCreate} handleClose={() => setOpenCreate(false)} />

      <div
        role="tabpanel"
        id="requests-inbox-panel"
        aria-labelledby={`requests-inbox-tab-${view}`}
        className="flex flex-col gap-4"
      >
        {view === 'outgoing' ? <PropertyList embedded /> : <RequestsList embedded />}
      </div>
    </section>
  )
}

export default RequestsInbox
